import React from "react";
import styled, { css, useTheme } from "styled-components";
import { CircleAlert, Trash2 } from "lucide-react";
import { MenuProps } from "antd";
import { DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { ActionsDropdownIcon } from "common/styles";
import LookIcon from "common/Icons/LookIcon";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";
import Flex from "common/Boxes/Flex";
import Dropdown from "common/Dropdown/Dropdown";
import useConfirmModal from "common/Modal/useConfirmModal";
import { useConfirmModalWidth } from "features/users-management/hooks";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { displayResultMessage } from "utils/misc.tsx";
import { hexToRGB } from "@shared/utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces, ClientStatus, ClientData } from "@shared/types";
import { useChangeAccountStatus } from "@shared/features/configuration/hooks";
import { ApiSlotGroup, UserGroup } from "features/users-management/types";
import RowActionsTrigger from "features/admin/NewLook/RowActionsTrigger";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

interface RowActionsProps {
  type: RowActionsType;
  account?: ClientData;
  activeAccountsInSlotCount?: number;
  slot?: ApiSlotGroup;
  user?: UserGroup;
  className?: string;
}

const RowActions: React.FC<RowActionsProps> = ({
  type,
  account,
  activeAccountsInSlotCount,
  slot,
  user,
  className,
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.USERS_MANAGEMENT);
  const theme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const modalWidth = useConfirmModalWidth();
  const { handleChangeClientStatus, isLoading } = useChangeAccountStatus(
    undefined,
    displayResultMessage
  );

  const getActionConfig = () => {
    switch (type) {
      case RowActionsType.ACCOUNT: {
        if (!account) return null;
        const isFreeSlot =
          account.client_status === ClientStatus.ACTIVE &&
          activeAccountsInSlotCount === 1;
        return {
          modalTitle: t("delete_account_title"),
          actionLabel: t("delete_account_label"),
          confirmMessage: isFreeSlot
            ? t("delete_account_confirm_free_slot")
            : t("delete_account_confirm"),
          warningContent: (
            <Flex $flexDirection="column" $alignItems="flex-start">
              {isFreeSlot ? (
                <WarningText>
                  {t("delete_account_warning_free_slot")}
                </WarningText>
              ) : (
                <>
                  <WarningText>{t("delete_account_warning_1")}</WarningText>
                  <WarningText style={{ marginTop: 8 }}>
                    <EmphasisText>{t("delete_account_warning_2")}</EmphasisText>
                  </WarningText>
                </>
              )}
            </Flex>
          ),
          onConfirm: () =>
            handleChangeClientStatus([account.client_id], ClientStatus.DELETED),
        };
      }
      case RowActionsType.SLOT: {
        if (!slot) return null;
        const freesSlot = slot.accounts.some(
          (c) => c.client_status === ClientStatus.ACTIVE
        );
        const freeCount = freesSlot ? 1 : 0;
        return {
          modalTitle: t("delete_slot_title"),
          actionLabel: t("delete_slot_label"),
          confirmMessage: t("delete_slot_confirm", { count: freeCount }),
          warningContent: (
            <WarningText>
              {t("delete_slot_warning", { count: freeCount })}
            </WarningText>
          ),
          onConfirm: () => {
            const clientIds = slot.accounts.map((c) => c.client_id);
            return handleChangeClientStatus(clientIds, ClientStatus.DELETED);
          },
        };
      }
      case RowActionsType.USER: {
        if (!user) return null;
        const count = user.apiSlots.filter((slot) =>
          slot.accounts.some(
            (account) => account.client_status === ClientStatus.ACTIVE
          )
        ).length;
        return {
          modalTitle: t("remove_user_title"),
          actionLabel: t("remove_user_label"),
          confirmMessage: t("remove_user_confirm", { count }),
          warningContent: (
            <WarningText>{t("remove_user_warning", { count })}</WarningText>
          ),
          onConfirm: () => {
            const clientIds = user.apiSlots.flatMap((slot) =>
              slot.accounts.map((c) => c.client_id)
            );
            return handleChangeClientStatus(clientIds, ClientStatus.DELETED);
          },
        };
      }
    }
  };

  const config = getActionConfig();

  const { openConfirmModal, confirmModalContextHolder } = useConfirmModal({
    title: config?.modalTitle || "",
    loading: isLoading,
    modalWidth,
  });

  if (!config) return null;

  const menuItems: MenuProps["items"] = [
    {
      key: "warning",
      type: "group",
      label: (
        <MenuWarningBox $isBottomNavigator={isBottomNavigator}>
          <Flex $alignItems="flex-start" $gap={8}>
            <LookIcon
              classic={
                <ExclamationCircleOutlined
                  style={{ color: theme.colors.orange, marginTop: 2 }}
                />
              }
              newLook={
                <CircleAlert
                  size={14}
                  strokeWidth={NEW_LOOK_ICON_STROKE}
                  color={theme.colors.orange}
                  style={{ flex: "none", marginTop: 1 }}
                />
              }
            />
            <WarningText>{config.warningContent}</WarningText>
          </Flex>
        </MenuWarningBox>
      ),
    },
    {
      key: "action",
      label: config.actionLabel,
      icon: (
        <LookIcon
          classic={<DeleteOutlined />}
          newLook={<Trash2 size={15} strokeWidth={NEW_LOOK_ICON_STROKE} />}
        />
      ),
      danger: true,
      onClick: () => {
        openConfirmModal(
          config.confirmMessage.split("\n\n"),
          config.onConfirm,
          true,
          false,
          config.actionLabel,
          t("cancel")
        );
      },
    },
  ];

  return (
    <div className={className} onClick={(e) => e.stopPropagation()}>
      {confirmModalContextHolder}
      <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
        {theme.isNewLook ? <RowActionsTrigger /> : <ActionsDropdownIcon />}
      </Dropdown>
    </div>
  );
};

export default RowActions;

export enum RowActionsType {
  ACCOUNT = "ACCOUNT",
  SLOT = "SLOT",
  USER = "USER",
}

const MenuWarningBox = styled.div<{ $isBottomNavigator?: boolean }>`
  padding: 12px;
  background: ${(p) => hexToRGB(p.theme.colors.yellow, 0.05)};
  border-radius: 4px;
  margin: 4px 0;
  max-width: ${(p) => (p.$isBottomNavigator ? "none" : "250px")};

  ${(p) =>
    p.$isBottomNavigator &&
    `
    margin: 4px;
  `}
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* A warning, so the stop colour - quietly. */
      border-radius: ${NEW_LOOK_RADIUS.control}px;
      background: ${hexToRGB(getNewLookTokens(p.theme).stop, 0.08)};
    `}
`;

const WarningText = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.colors.secondaryGrey};
  line-height: 1.4;
  flex: 1;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      ${newLookType.meta};
      color: ${getNewLookTokens(p.theme).secondary};
    `}
`;

const EmphasisText = styled.span`
  color: ${(p) => p.theme.colors.yellow};
  font-weight: 600;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      color: ${getNewLookTokens(p.theme).ink};
      font-weight: 500;
    `}
`;
